import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import ScriptDetails from 'pages/scripts/components/ScriptDetails';
import ScriptCustomToolbar from 'pages/scripts/components/ScriptCustomToolbar';
import MDBox from 'components/MDBox';
import {
  useBulkDeleteScriptsMutation,
  useCreateScriptMutation,
  useGetScriptsQuery,
  useUpdateScriptMutation
} from 'services/scriptApi';
import './scripts.css';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';

/**
 * Component for managing Groovy scripts, including displaying a list of scripts, adding, updating,
 * and deleting scripts. Uses a DataGrid for listing the scripts and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Scripts />
 *
 * @returns {React.Component} The Scripts management component.
 */
export default function Scripts() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const apiRef = useGridApiRef();

  const {
    data: scripts = [],
    error: scriptsError,
    isLoading: scriptsLoading
  } = useGetScriptsQuery();
  const [bulkDeleteScript] = useBulkDeleteScriptsMutation();
  const [createScript] = useCreateScriptMutation();
  const [updateScript] = useUpdateScriptMutation();

  const [selectedScript, setSelectedScript] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const columns = [
    {
      field: 'code',
      headerName: msg({ id: `page.scripts.table.column.code` }),
      minWidth: 350,
      maxWidth: 400
    },
    {
      field: 'name',
      headerName: msg({ id: `page.scripts.table.column.name` }),
      minWidth: 350,
      maxWidth: 400
    },
    {
      field: 'size',
      headerName: msg({ id: `page.scripts.table.column.size` }),
      minWidth: 100,
      maxWidth: 100,
      renderCell: (params) => {
        if (params.value == null) return '';
        return `${params.value} B`;
      }
    },
    {
      field: 'createdDate',
      headerName: msg({ id: `page.scripts.table.column.createdDate` }),
      minWidth: 180,
      maxWidth: 180,
      renderCell: (params) => {
        if (!params.value) return '';
        try {
          return new Date(params.value).toLocaleString();
        } catch {
          return '';
        }
      }
    },
    {
      field: 'modifiedDate',
      headerName: msg({ id: `page.scripts.table.column.modifiedDate` }),
      minWidth: 180,
      maxWidth: 180,
      renderCell: (params) => {
        if (!params.value) return '';
        try {
          return new Date(params.value).toLocaleString();
        } catch {
          return '';
        }
      }
    },
    {
      field: 'custom',
      headerName: msg({ id: `page.scripts.table.column.isCustom` }),
      minWidth: 180,
      maxWidth: 200,
      type: 'boolean'
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedScript(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    // Filter to only include custom scripts
    const customScriptIds = rowSelectionModel.filter((id) => {
      const script = scripts.find((s) => s.code === id);
      return script && script.custom;
    });
    setRowSelectionModel(customScriptIds);
  };

  const handleAdd = () => {
    setSelectedScript(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows && selectedRows.length > 0) {
      bulkDeleteScript(selectedRows.map((row) => row.code))
        .unwrap()
        .then((response) => {
          // Check if there were any failed deletions
          if (response.failed && response.failed.length > 0) {
            addNotification({
              type: 'warning',
              title: msg({ id: 'notification.warning.title' }),
              message: msg({ id: 'page.scripts.delete.partial' })
            });
          } else {
            addNotification({
              type: 'success',
              title: msg({ id: 'notification.success.title' }),
              message: msg({ id: 'page.scripts.delete.success' })
            });
          }
          clearScriptSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.scripts.delete.error' })
          });
        });
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.code) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const selectedRows = rowSelectionModel
    .map((code) => scripts.find((script) => script.code === code))
    .filter((script) => script && script.custom);

  if (scriptsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.scripts.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            apiRef={apiRef}
            rows={scripts}
            columns={columns}
            getRowId={(row) => row.code}
            isRowSelectable={(params) => params.row.custom}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'code', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: ScriptCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                selectedRows,
                // Only enable actions if selected scripts are all custom
                areActionsEnabled:
                  selectedRows.length > 0 && selectedRows.every((row) => row.custom)
              }
            }}
            loading={scriptsLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => {
              let classes = [];
              if (params.id === selectedRowId) classes.push('Mui-selected');
              if (!params.row.custom) classes.push('read-only-row');
              return classes.join(' ');
            }}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 800, p: 2 }
          }}
        >
          <ScriptDetails
            script={selectedScript}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
            readOnly={selectedScript && !selectedScript.custom}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearScriptSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedScript(null);
    setSelectedRowId(null);
  }

  function handleCreate(data) {
    createScript({ name: data.name, content: data.content })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.scripts.create.success' })
        });
        clearScriptSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.scripts.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateScript({
      code: data.code,
      content: data.content
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.scripts.update.success' })
        });
        clearScriptSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.scripts.update.error' })
        });
      });
  }
}
